import React, { Fragment, useState } from 'react';
import { Create, CreateBase, CreateProps, DateInput, required, ShowBase, Show, SimpleForm, TextInput, useTranslate, TextField, DateField, SimpleShowLayout, TopToolbar, EditButton, useCreate, useRecordContext, ReferenceField, ReferenceManyField, SimpleList, Datagrid, BooleanField, ImageInput, ImageField, TextFieldProps, useUpdate, RefreshButton, FunctionField } from 'react-admin';
import { Client } from '../types';
import { dissolutionLocationLabel, legacyDissolutionHint } from './dissolutionLocation';
import ClientLinkedContractsList from './ClientLinkedContractsList';
import ClientShowToolBar from './ClientShowToolBar';
import { Grid, Box, Typography, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Paper } from '@mui/material';
import { ReferenceManyToManyField } from "@react-admin/ra-relationships";
import ClientLinkedFuneralsList from './ClientLinkedFuneralsList';
import UserProfileField from '../components/UserProfileField';
import Avatar from '@mui/material/Avatar';
import { CreateInDialogButton } from '@react-admin/ra-form-layout';
import ClientEditBase from './ClientEditBase';
import { useRefresh } from 'react-admin';
import SpouseField from './SpouseField';
import { ClientImage } from './ClientImage';
import SinField from './SinField';


const ClientShow = () => {
  const translate = useTranslate();

  return( 
    <Show actions={<ClientShowToolBar />}>
      <ClientBaseShow />
    </Show>
  );
  }

  const ClientBaseShow = () => {
    const record = useRecordContext<Client>();

    const imageSrc = process.env.REACT_APP_SUPABASE_URL + '/storage/v1/object/public/client-images' + '/clients/6/image.png';

    return (record ?
    <div>
    <ClientTitle />
      <Grid container spacing={2}>
        <Grid item xs={10} sm={6}>
            <SimpleShowLayout>
              <TextField source="prefix" />
              <TextField source="name" />
              <TextField source="hebrew_name" />
              <TextField source="address" />
              <TextField source="address2" />
              <TextField source="city" />
              <TextField source="province" />
              <TextField source="postal_code" />
              <TextField source="country" />
              <TextField source="email" />
              <BooleanField source="send_condolences" />
              <TextField source="phone_number" />
              <TextField source="mobile_phone_number" />
              <BooleanField source="bad_debtor" />
          </SimpleShowLayout>
        </Grid>
        <Grid item xs={10} sm={6}>
          <SimpleShowLayout>
              <BooleanField source="deceased" />
              <TextField source="occupation" />
              <TextField source="industry" />
              <BooleanField source="retired" />
              <TextField source="medicare_number" />
              <TextField source="drivers_license" />
              <SinField source="sin" label="SIN" />
              <DateField source="birthdate" options={{ timeZone: 'UTC' }} />
              <TextField source="place_of_birth" />
              <TextField source="sex" />
              <ReferenceField reference='marital_statuses' source='marital_status_id' link={false}>
                <TextField source="name" />
              </ReferenceField>
              <SpouseField label="Spouse"/>
              {record.legacy_spouse ? <SpouseImportField label="Spouse (Import)"/> : null }
              <DateField source="date_of_marriage" options={{ timeZone: 'UTC' }} />
              <TextField source="place_of_marriage" />
              <DateField source="date_of_marriage_dissolution" options={{ timeZone: 'UTC' }}/>
              <FunctionField
                label="Where marriage was dissolved (DEC)"
                render={(r: any) => dissolutionLocationLabel(r?.marriage_dissolution_location) ?? legacyDissolutionHint(r) ?? ''}
              />
              <TextField source="parent1_name" label="Parent 1 (Father)" />
              <TextField source="parent2_name" label="Parent 2 (Mother)" />
              <BooleanField source="father_alive" />
              <BooleanField source="mother_alive" />

            </SimpleShowLayout>
          </Grid>

        <Grid item spacing={2} xs={12} sm={10} mx={2}>
          <ChildrenList />
        </Grid>

        <Grid item xs={12} sm={10} mx={2}>
          <ClientImage />
        </Grid>



        <Grid item xs={12} sm={10} mx={2}>
          <ClientLinkedFuneralsList />
          {/* <ClientLinkedContractsList /> */}
        </Grid>
      </Grid>
    </div>  
    : null);
  }


  const SpouseImportField = (props:TextFieldProps) => {
    const record = useRecordContext<Client>();

    if(record?.legacy_spouse?.length > 0){

    return(<TextField source="legacy_spouse" />);
    }
    else{
      return null;
    }
  }

  const ClientTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return(
      <>
      <Stack direction="row" justifyContent="space-between">
        <Box sx={{display: 'flex', m: 2}}> 
          {/* <Avatar>
          </Avatar> */}
          <Typography mt={0.5} pl={1} variant='h5'>
            <TextField source="name" variant='h5'/>
          </Typography>
        </Box>
        <ReferenceField reference='profiles' source='owner_id' label="Owner" link={false}>
          <UserProfileField size='25'/>
        </ReferenceField>
      </Stack>
      <Divider sx={{mx: 2, mb: 1}}/>
      </>
    )
    // return <span>Funeral for {record.name}</span>;
  };

  const ChildrenList = () => {
    const record = useRecordContext<Client>();
    const translate = useTranslate();
    
    if (!record?.children || record.children.length === 0) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {translate('resources.clients.fields.children')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {translate('resources.clients.no_children')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {translate('resources.clients.fields.children')}
        </Typography>
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate('resources.clients.fields.firstName')}</TableCell>
                <TableCell>{translate('resources.clients.fields.lastName')}</TableCell>
                <TableCell>{translate('resources.clients.fields.birthdate')}</TableCell>
                <TableCell>{translate('resources.clients.fields.isOver18')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {record.children.map((child, index) => (
                <TableRow key={index}>
                  <TableCell>{child.firstName}</TableCell>
                  <TableCell>{child.lastName}</TableCell>
                  <TableCell>{new Date(child.birthdate).toLocaleDateString()}</TableCell>
                  <TableCell>{child.isOver18 ? '✓' : '✗'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  };


export default ClientShow;